import { Injectable, Injector } from '@angular/core';
import {
  get,
  includes,
  isEmpty,
  isEqual,
  isNil,
  map,
  some,
  find,
  reduce,
  set,
  remove,
  merge,
  compact,
  omit,
  head,
  cloneDeep,
  has,
  unset,
  forEach,
} from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { rx, Subscription } from '@shared/rx';

import { ValidationService } from '@components/form/services/validation/validation.service';
import { SitesService } from '../sites';
import { QohService } from '../qoh';
import {
  CreateFormRules,
  ENTITY_TYPES,
  FromAndToSiteId,
  IBaseEntityResponse,
  IBaseEntityType,
  IBaseFormService,
  ISite,
  ITransferFormFieldPath,
  TRANSFER_TYPE,
  GridRowData,
  XuxGridAction,
  IGrandTotal,
  IGridInventoryItemVenueTotalFormData,
  ObjectId,
  Primitive,
} from '@shared/types';
import { createFormRules as createTransferFormRules } from './form-rules';
import { lazyInject } from '@containers/base/decorators';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { TransferTotalFormService } from '@shared/services/transfer/transfer-totals-form.service';
import { InventoryItemVenueService } from '@app/shared/services/inventory-item-venue';
import { setGridKeyboardSupport, getNewTransferStatus } from '@shared/utils';
import { TRANSFER_STATUS_VALUE } from '@shared/types/enums/transfer-status.enum';
import {
  getFormControl,
  getFormFieldValue,
  setFormFieldValue,
} from '@components/form/services/schema-form/schema-form-functions';
import { ITreeData } from '@shared/types/common/tree.type';
import { XprtDataService } from '../xprt-data';
import {
  IPageJumpRouteState,
  IQohInventoryItemVenue,
  ITransferData,
  ReallyChangedTransfers,
  IGridFlatInventoryItemVenueTotal,
  ITransferItem,
} from '@shared/types/entities';
import {
  appendInitialFactorAndCountValue,
  getIsShowReceivedColStatus,
  initInventoryThresholdMapper,
  calculateUnitCost,
  updateInitialFactorAndCountValue,
  setParQtyColumnVisible,
} from '../transfer/transfer.util';
import {
  DmDataService,
  ScanningService,
  TransferGroupService,
} from '@shared/services';
import { select } from '@angular-redux/store';
import { InventoryItemVenue } from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue';
import { DecimalNumber } from '@app/shared/common';
import {
  TRANSFER_FINAL_STATUS,
  TRANSFER_UNSAVED_STATUS,
} from '@shared/services/transfer/transfer.definition';
import { CompanyUtilService } from '../company';
import { TransferBaseFormService } from '@shared/services/transfer-common';
import { TransferEditContainerComponent } from '@app/containers/transfer/containers';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { ColumnApi, GridApi } from 'ag-grid-community';
import { setColumnDef } from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { EMPTY_CHANGED_ITEMS } from '@shared/services/transfer-group/utils/transfer-items-utils';
import { ENTITY_ID_PATH } from '@config/global-constants';
import { TransferAutoRestockService } from '../transfer-auto-restock/transfer-auto-restock.service';
import { StandWorksheetCalcServiceProvider } from '../stand-worksheet/calculation-services/stand-worksheet-calc-service-provider';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class TransferGroupChildFormService
  extends TransferBaseFormService
  implements IBaseFormService<ENTITY_TYPES.TRANSFER>
{
  public get groupId(): string {
    return this.route.snapshot.params?.groupId;
  }
  public static ENTITY_TYPE: ENTITY_TYPES = ENTITY_TYPES.TRANSFER;

  @select(['entities', ENTITY_TYPES.TRANSFER, 'currentDocument'])
  public currentDocument$: Observable<any>;

  @lazyInject(TransferGroupService)
  public transferGroupService: TransferGroupService;
  @lazyInject(ValidationService) public validationService: ValidationService;
  @lazyInject(TransferTotalFormService)
  public totalFromService: TransferTotalFormService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(QohService) public qohService: QohService;
  @lazyInject(CompanyUtilService) public companyService: CompanyUtilService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(TransferAutoRestockService)
  public transferAutoRestockService: TransferAutoRestockService;
  @lazyInject(StandWorksheetCalcServiceProvider)
  public standWorksheetCalcServiceProvider: StandWorksheetCalcServiceProvider;
  @lazyInject(ScanningService)
  public scanningService: ScanningService;

  public form: FormGroup;
  protected subscriptions: Subscription[] = [];
  public createFormRulesFunction: CreateFormRules<TransferGroupChildFormService> =
    createTransferFormRules;
  public inventoryItemVenueMap: IBaseEntityType = {};

  public formFieldPath: ITransferFormFieldPath = {
    category: 'category',
    count: 'count',
    departmentCodeTo: 'site_department_code_to',
    eventId: 'event_id',
    glAccountName: 'gl_account.name',
    initialCount: 'initial_count',
    inventoryItemVenues: 'inventory_items',
    isChargeable: 'is_chargeable',
    itemCost: 'item_cost',
    measureEntityId: 'measure_entity_id',
    measureFactorValue: 'measure_factor_value',
    quantityReversed: 'quantity_reversed',
    receivedTotalCost: 'received_total_cost',
    reversedCost: 'reversed_cost',
    siteId: 'site_id',
    siteIdTo: 'site_id_to',
    siteName: 'site_name',
    siteNameTo: 'site_name_to',
    status: 'status',
    storeNumber: 'store_number',
    storeNumberTo: 'store_number_to',
    totalCost: 'total_cost',
    transactionDate: 'transaction_date',
    transferType: 'transfer_type',
    type: 'type',
    virtualInventoryItemVenues: 'virtual.inventory_items',
    virtualTotals: 'virtual.totals',
    virtualTransactionDate: 'virtual.transaction_date',
    virtualSelectedEvent: 'virtual.selected_event',
    virtualReallyChangedTransfers: 'virtual.really_changed_transfers',
    qohFrom: 'qoh_from',
    virtualFromSite: 'virtual.from_site_name',
    virtualToSite: 'virtual.to_site_name',
  };

  public transferDateDisable: boolean = false;
  public selectedSite: ISite;

  public isShownSubmittedColumns$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  public grandTotalData: IGrandTotal;

  public constructor(public injector: Injector) {
    super(injector);
  }

  public onFormReady(form: FormGroup): void {
    this.form = form;

    // check it should add submitted columns to total form
    this.isShownSubmittedColumns$.next(this.getIsShownQuantityReceivedCols());
    this.setOpenStatusForm();
    this.initItemListSubscription();
    this.initFetchingQohAndQtyDataStatus();
    this.initRouteStateSubscription();
    this.inventoryItemVenuesChangeSubscription();
    this.generateVoidAllowedMap([this.document]);
    this.initKeyDownEventSubscription();
  }

  public getGridReadyHandler(
    editor: TransferEditContainerComponent,
  ): (action: XuxGridAction, grid: XuxEditGridComponent) => void {
    return (action: XuxGridAction, grid: XuxEditGridComponent): void => {
      this.xuxGrid = grid;
      this.listGridApi = action.payload?.gridApi;
      this.scanningService.gridApi = this.listGridApi;
      this.scanningService.columnApi =
        action?.payload?.gridApi?.['columnController']?.columnApi;
      if (!editor.canEdit || this.isFinalStatusFun()) {
        // disable arrow key
        setGridKeyboardSupport(grid.xuxGridComponent, false);
      }
      const status = getFormFieldValue(this.form, this.formFieldPath.status);
      setParQtyColumnVisible(this.listGridApi, status);
      this.defineListColumnDisplay(action);
      action.payload?.gridApi.expandAll();
    };
  }

  public defineListColumnDisplay(action: XuxGridAction): void {
    const columnApi: ColumnApi =
      action.payload?.gridApi['columnController'].columnApi;
    const gridApi: GridApi = action.payload?.gridApi;

    if (this.getIsShownQuantityReceivedCols()) {
      // shown initial request count col
      columnApi.setColumnsVisible([this.formFieldPath.count], true);

      setColumnDef(gridApi, this.formFieldPath.totalCost, {
        headerName: this.i18nService.translate(
          I18n.Forms.TransferForm.ReceivedCost,
        ),
      });
    }
    if (!this.isFinalStatusFun()) {
      this.showTransferUnitTooltips(
        gridApi,
        this.formFieldPath.measureEntityId,
      );
    }
    const siteFromId = getFormFieldValue(this.form, this.formFieldPath.siteId);
    this.isQohFromVisible = this.transferService.getDisplayQohFrom(
      siteFromId,
      this.transferGroupService.companySitesList,
    );
    columnApi?.setColumnsVisible(
      [this.formFieldPath.qohFrom],
      this.isQohFromVisible,
    );
    gridApi.sizeColumnsToFit();
  }

  public destroyFormDefinition(): void {
    this.hiddenRemoveInfo$.next(true);
    this.hiddenModifiedInfo$.next(true);
    this.hiddenThresholdInfo$.next(true);
    this.isInventoryItemVenuesGridBusy$.next(false);
    this.grandTotalData = {
      request_cost: 0,
      received_cost: 0,
      reversed_cost: 0,
    };

    super.destroyFormDefinition();
    this.scanningService.destroyServiceDefinition();
  }

  public combineApiDataWithLocalChange(
    items: IQohInventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    const siteIdTo: string = this.getFormFieldValue(
      this.formFieldPath.siteIdTo,
    );
    const unavailableItems: Record<string, { item_entity_id: string }> = get(
      this.form.value,
      'unavailable_items',
      {},
    );
    const updatedItems: Record<
      string,
      { item_entity_id: string; measure_entity_id?: string }
    > = get(this.form.value, 'updated_items', {});
    const virtualInventoryItems: ITransferData[] =
      this.getFormFieldValue(this.formFieldPath.virtualInventoryItemVenues) ??
      [];
    const allChangedTransfers: ReallyChangedTransfers = this.getFormFieldValue(
      this.formFieldPath.virtualReallyChangedTransfers,
    );
    const reallyChangedTransfer = get(
      allChangedTransfers,
      siteIdTo,
      cloneDeep(EMPTY_CHANGED_ITEMS),
    );

    if (!isEmpty(reallyChangedTransfer.items)) {
      let needMarkDirty: boolean = this.getFormFieldDirty(
        this.formFieldPath.inventoryItemVenues,
      );

      const newReallyChangedItems = compact(
        map(reallyChangedTransfer.items, (changedItem) => {
          const targetVirtualItem = find(virtualInventoryItems, {
            item_entity_id: changedItem.entity_id,
          });
          const targetInventoryItem = find(items, {
            entity_id: changedItem.entity_id,
          });

          const unavailableItem = find(unavailableItems, {
            item_entity_id: changedItem.entity_id,
          });
          const updatedItem = find(updatedItems, {
            item_entity_id: changedItem.entity_id,
          });

          // can not find item both in unavailable items and virtual Inventory items, delete it
          if (
            targetVirtualItem?.error &&
            isEmpty(omit(targetInventoryItem, ['item_entity_id', 'error'])) &&
            (updatedItem || unavailableItem)
          ) {
            // remove target item from virtualReallyChangedTransfers
            return null;
          }

          if (!isNil(targetInventoryItem)) {
            needMarkDirty = true;

            merge(targetInventoryItem, changedItem);
          }

          return changedItem;
        }),
      );

      reallyChangedTransfer.items = newReallyChangedItems;

      set(allChangedTransfers, siteIdTo, reallyChangedTransfer);
      setFormFieldValue(
        this.form,
        this.formFieldPath.virtualReallyChangedTransfers,
        allChangedTransfers,
        false,
      );

      if (needMarkDirty) {
        this.getFormFieldControl(
          this.formFieldPath.inventoryItemVenues,
        )?.markAsDirty();
      }
    }

    return items;
  }

  public getReallyChangedItem(
    status: TRANSFER_STATUS_VALUE,
    updateItem: IQohInventoryItemVenue | GridRowData,
    targetVirtualItem: IQohInventoryItemVenue,
  ): Record<string, Primitive | object> {
    const editFieldsMapper = {
      [TRANSFER_STATUS_VALUE.Open]: [
        this.formFieldPath.initialCount,
        this.formFieldPath.measureEntityId,
      ],
      [TRANSFER_STATUS_VALUE.Pending]: [
        this.formFieldPath.count,
        this.formFieldPath.initialCount,
        this.formFieldPath.measureEntityId,
      ],
    };
    const isPreEvent = this.isPreEventTypeTransfer();
    const editFieldStatus =
      isPreEvent && isEqual(TRANSFER_STATUS_VALUE.Pending, status)
        ? TRANSFER_STATUS_VALUE.Open
        : status;
    const editFields = includes(TRANSFER_UNSAVED_STATUS, status)
      ? [this.formFieldPath.initialCount, this.formFieldPath.measureEntityId]
      : editFieldsMapper[editFieldStatus];

    return reduce(
      editFields,
      (item, field) => {
        const updateItemValue = get(updateItem, field);
        const oldItemValue = get(targetVirtualItem, field);

        let isSame =
          isEqual(oldItemValue, updateItemValue) ||
          isEqual(oldItemValue?.toString(), updateItemValue?.toString());

        if (
          !targetVirtualItem &&
          isEqual(field, this.formFieldPath.measureEntityId)
        ) {
          const dmInventoryItem = find(
            this.allInventoryItems,
            (inventoryItem) => {
              return isEqual(updateItem.entity_id, inventoryItem.entity_id);
            },
          );

          const defaultMeasure = find(dmInventoryItem?.measures, {
            transactions: { transfer: true },
          });

          isSame = isEqual(defaultMeasure?.entity_id, updateItemValue);
        }

        if (!isSame) {
          if (
            isPreEvent &&
            isEqual(status, TRANSFER_STATUS_VALUE.Pending) &&
            isEqual(field, this.formFieldPath.initialCount)
          ) {
            item[this.formFieldPath.count] = updateItemValue;
          }
          item[field] = updateItemValue;
        }

        return item;
      },
      {},
    );
  }

  public getUpdatedReallyChangedTransfers(
    siteIdTo: string,
    status: TRANSFER_STATUS_VALUE,
    updateItem: IQohInventoryItemVenue | GridRowData,
    remoteInventoryItems: IQohInventoryItemVenue[],
  ): ReallyChangedTransfers {
    const targetVirtualItem: IQohInventoryItemVenue = find(
      remoteInventoryItems,
      (item) => isEqual(updateItem?.entity_id, item.item_entity_id),
    );

    const reallyChangedItem = this.getReallyChangedItem(
      status,
      updateItem,
      targetVirtualItem,
    );

    const reallyChangedTransfers: ReallyChangedTransfers =
      this.getFormFieldValue(this.formFieldPath.virtualReallyChangedTransfers);

    const alreadyChangedTransfer = get(
      reallyChangedTransfers,
      siteIdTo,
      merge(cloneDeep(EMPTY_CHANGED_ITEMS), {
        originItems:
          this.getFormFieldValue(
            this.formFieldPath.virtualInventoryItemVenues,
          ) ?? [],
      }),
    );
    remove(alreadyChangedTransfer.items, { entity_id: updateItem.entity_id });

    const changedItem = this.buildChangedItemWithRemote(
      reallyChangedItem,
      updateItem,
      targetVirtualItem,
    );

    if (!isEmpty(changedItem)) {
      alreadyChangedTransfer.items.push(changedItem);
    }

    if (isEmpty(alreadyChangedTransfer.items)) {
      // remove siteIdTo property in the really changed transfers when all changed inventory items is empty
      unset(reallyChangedTransfers, siteIdTo);
    } else {
      set(reallyChangedTransfers, siteIdTo, alreadyChangedTransfer);
    }

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualReallyChangedTransfers,
      reallyChangedTransfers,
      false,
    );

    return reallyChangedTransfers;
  }

  public updateSavedInventoryItems(
    updateItem: IQohInventoryItemVenue | GridRowData,
  ): void {
    super.updateSavedInventoryItems(updateItem);

    const siteIdTo: string = this.getFormFieldValue(
      this.formFieldPath.siteIdTo,
    );
    const status: TRANSFER_STATUS_VALUE = this.getFormFieldValue(
      this.formFieldPath.status,
    );
    const remoteInventoryItems: IQohInventoryItemVenue[] =
      this.getFormFieldValue(this.formFieldPath.virtualInventoryItemVenues);

    const reallyChangedTransfers: ReallyChangedTransfers =
      this.getUpdatedReallyChangedTransfers(
        siteIdTo,
        status,
        updateItem,
        remoteInventoryItems,
      );
    const alreadyChangedTransfer = get(reallyChangedTransfers, siteIdTo);

    // pristine when change is empty
    if (isEmpty(alreadyChangedTransfer?.items)) {
      // need this time out to make sure we set Pristine after row updated
      setTimeout(() => {
        this.getFormFieldControl(
          this.formFieldPath.inventoryItemVenues,
        ).markAsPristine();
      }, 0);
    }
  }

  public getInventoryItemVenuesRequireValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlRoot = control.root;
      const controlParent = control.parent;

      if (isNil(controlRoot.value) || isNil(controlParent)) return null;

      const transferTypeValue: TRANSFER_TYPE =
        controlRoot.value?.[this.formFieldPath.transferType];
      const statusValue: TRANSFER_STATUS_VALUE =
        controlRoot.value?.[this.formFieldPath.status];

      // just return when form status is not open or new
      if (includes(TRANSFER_FINAL_STATUS, statusValue)) {
        return null;
      }

      const data: IQohInventoryItemVenue[] =
        this.getFlattenInventoryItemVenueData<IQohInventoryItemVenue>(
          control.value,
        );
      const hasQuantityItems = some(data, (item) => {
        const castNumber = new DecimalNumber(
          item[this.formFieldPath.initialCount],
        );
        const receivedNumber = new DecimalNumber(
          item[this.formFieldPath.count],
        );

        return getIsShowReceivedColStatus(transferTypeValue, statusValue)
          ? castNumber.gt(0) ||
              castNumber.lt(0) ||
              receivedNumber.gt(0) ||
              receivedNumber.lt(0)
          : castNumber.gt(0) || castNumber.lt(0);
      });

      return hasQuantityItems
        ? null
        : { error: I18n.ErrorMessages.TransferNoQuantityItemsError };
    };
  }

  public inventoryItemVenuesChangeSubscription(): void {
    this.subscriptions.push(
      this.fieldValueChanges(this.formFieldPath.inventoryItemVenues).subscribe(
        (inventoryItems) => {
          const status: TRANSFER_STATUS_VALUE = this.getFormFieldValue(
            this.formFieldPath.status,
          );
          const newStatus = getNewTransferStatus(status, inventoryItems, false);
          setFormFieldValue(
            this.form,
            this.formFieldPath.status,
            newStatus,
            false,
          );
        },
      ),
    );
  }

  public initItemTotalsSubscription(): void {
    const subscription = this.totalFromService.handleTotalsSubscription(
      this,
      this.pickGrandTotalFields.bind(this),
    );
    this.subscriptions.push(subscription);
  }

  public setOpenStatusForm(): void {
    if (this.isEditMode() && !this.isFinalStatusFun()) {
      this.getFormFieldControl(
        this.formFieldPath.inventoryItemVenues,
      ).updateValueAndValidity();
      this.setInfoMessage();
    }
  }

  public async getInventoryItemVenues(
    [siteId, siteIdTo]: FromAndToSiteId,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    inventoryItems: ITreeData[],
  ): Promise<void> {
    this.isInventoryItemVenuesGridBusy$.next(true);
    if (this.isFinalStatusFun()) {
      const inventoryItemVenues = getFormFieldValue(
        this.form,
        this.formFieldPath.virtualInventoryItemVenues,
      );
      const entityIds = map(inventoryItemVenues, 'item_entity_id');
      this.fetchItemsUPCCodes(entityIds, inventoryItemVenues).subscribe(
        (inventoryItem: ITransferItem[]) => {
          const treeData = this.getFinalStatusItemsTreeData(inventoryItem);

          this.updateItemsByCompanyPreferences(
            treeData,
            this.formFieldPath.inventoryItemVenues,
            false,
          );
          this.isQohDataFetching = false;
          this.isPendingQuantityFetching = false;
          this.isInventoryItemVenuesGridBusy$.next(false);
          if (this.isCategoryListView()) {
            setTimeout(() => {
              this.listGridApi.expandAll();
            });
          }
        },
      );
    } else {
      const subscription = this.fetchInventoryItemVenues([siteId, siteIdTo])
        .pipe(
          rx.map((res: IBaseEntityResponse) => {
            return this.processInventoryItems(res);
          }),
          rx.tap(
            (res) =>
              (this.inventoryItemThresholdMapper = initInventoryThresholdMapper(
                res.items as IQohInventoryItemVenue[],
              )),
          ),
          rx.map((res: IBaseEntityResponse<InventoryItemVenue>) => {
            const inventoryItemVenues =
              this.takeIntersectionAndProcessThreshold(res, siteIdTo);

            return <IBaseEntityResponse<InventoryItemVenue>>{
              ...res,
              items: inventoryItemVenues,
              total: inventoryItemVenues.length,
            };
          }),
          rx.map((res: IBaseEntityResponse) =>
            this.mixinXVIData(res.items as InventoryItemVenue[]),
          ),
          rx.mergeMap((res: IQohInventoryItemVenue[]) =>
            this.getQuantityOnHandData(res, siteId, siteIdTo).pipe(
              rx.map(() => res),
            ),
          ),
          rx.mergeMap((res: IQohInventoryItemVenue[]) =>
            this.getPendingQuantity(siteId, siteIdTo).pipe(rx.map(() => res)),
          ),
          rx.tap(
            (res) =>
              (this.inventoryItemThresholdMapper =
                initInventoryThresholdMapper(res)),
          ),
          rx.map(this.addCountAndErrorToItems.bind(this)),
          rx.map((res: IQohInventoryItemVenue[]) => this.addQOHForItems(res)),
          rx.map((res: IQohInventoryItemVenue[]) =>
            appendInitialFactorAndCountValue(res),
          ),
          rx.map(this.combineApiDataWithLocalChange.bind(this)),
          rx.map((res) => this.processParQty(res)),
          rx.tap((res: IQohInventoryItemVenue[]) => {
            this.allInventoryItems = res;
          }),
          rx.tap(() => this.setThresholdInfoMessage()),
          rx.tap(this.updateInventoryItemVenues.bind(this)),
        )
        .subscribe(() => {
          if (
            isEqual(
              getFormFieldValue(this.form, this.formFieldPath.status),
              TRANSFER_STATUS_VALUE.Pending,
            )
          ) {
            this.updateGridByViewSetting({
              value: {
                hide_empty_rows: true,
              },
            });
          }
        });

      this.subscriptions.push(subscription);
    }
  }

  public mixinXVIData(
    inventoryItemVenues: InventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    if (isEmpty(inventoryItemVenues)) {
      return [];
    }

    return map(inventoryItemVenues, (item): IQohInventoryItemVenue => {
      return {
        ...item,
        [this.formFieldPath.initialCount]: get(
          this.inventoryItemVenueMap,
          [item.entity_id, this.formFieldPath.initialCount],
          0,
        ),
        [this.formFieldPath.itemCost]: get(
          this.inventoryItemVenueMap,
          [item.entity_id, this.formFieldPath.itemCost],
          calculateUnitCost({ data: { ...item } }).valueOf(),
        ),
        [this.formFieldPath.totalCost]: get(
          this.inventoryItemVenueMap,
          [item.entity_id, this.formFieldPath.totalCost],
          0,
        ),
        qoh_from: get(this.inventoryItemVenueMap, 'qoh_from', 0),
        qoh_to: get(this.inventoryItemVenueMap, 'qoh_to', 0),
      } as unknown as IQohInventoryItemVenue;
    });
  }

  public fetchInventoryItemVenues([
    siteId,
    siteIdTo,
  ]: FromAndToSiteId): Observable<IBaseEntityResponse> {
    const query = {
      filter: 'measures/transactions/transfer eq in(true)',
      select: [
        'is_chargeable',
        'code',
        'reporting_category_major',
        'reporting_category_minor',
        'name',
        'gl_account',
        'measures',
        'vendors',
        'preferred_vendor',
        'thresholds',
        'costs',
        'upc_codes',
      ],
    };

    return this.inventoryItemVenueService.fetchCurrentList(query, [
      siteId,
      siteIdTo,
    ]);
  }

  public addQOHForItems(
    inventoryItems: IQohInventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    return map(inventoryItems, (item) => {
      const qohTo = this.getQohAndPendingQuantity(
        item,
        this.formFieldPath.siteIdTo,
      );
      const qohFrom = this.getQohAndPendingQuantity(
        item,
        this.formFieldPath.siteId,
      );

      return { ...item, qohTo, qohFrom };
    });
  }

  public canPrintEmptyPdf(): boolean {
    const status = this.getFormStatus();

    return includes(
      [TRANSFER_STATUS_VALUE.Pending, TRANSFER_STATUS_VALUE.Open],
      status,
    );
  }

  public initRouteStateSubscription(): void {
    this.subscriptions.push(
      this.routeState$
        .pipe(rx.first())
        .subscribe((value: IPageJumpRouteState) => {
          if (!value?.selectedSite) {
            return;
          }

          const { selectedSite, storeForm } = value;
          if (isEqual(storeForm.status, TRANSFER_STATUS_VALUE.New)) {
            this.form.markAsDirty();
          }
          this.form.patchValue(storeForm);
          this.selectedEvent = get(
            storeForm,
            this.formFieldPath.virtualSelectedEvent,
          );
          this.initItemTotalsSubscription();
          this.getInventoryItemVenues(
            [storeForm.site_id, selectedSite.site_id_to],
            value.selectedSite.inventory_items,
          );
        }),
    );
  }

  public updateInventoryItemVenues(
    items: IQohInventoryItemVenue[],
    markField: boolean = false,
    _: boolean = false,
  ): void {
    // historical quantities create transfer should fill the Quantity Requested
    const updatedParInventoryItems: IQohInventoryItemVenue[] = map(
      items,
      (item) => ({
        ...item,
        initialFactorAndCountValue: updateInitialFactorAndCountValue(
          item as Partial<IQohInventoryItemVenue>,
        ),
      }),
    );

    if (markField) {
      const validInventoryItems = [];
      forEach(updatedParInventoryItems, (item) => {
        if (!isNil(item.initial_count)) {
          validInventoryItems.push({
            entity_id: get(item, ENTITY_ID_PATH),
            initial_count: get(item, this.formFieldPath.initialCount),
            item_entity_id: get(item, ENTITY_ID_PATH),
            _id: get(item, '_id'),
            count: get(item, this.formFieldPath.count),
            [this.formFieldPath.measureEntityId]: get(
              item,
              this.formFieldPath.measureEntityId,
            ),
          });
        }
      });

      if (!isEmpty(validInventoryItems)) {
        const originReallyChangedTransfer = this.getFormFieldValue(
          this.formFieldPath.virtualReallyChangedTransfers,
        );
        const siteIdTo: string = this.getFormFieldValue(
          this.formFieldPath.siteIdTo,
        );
        const currentTransferUnsavedData = get(
          originReallyChangedTransfer,
          siteIdTo,
        );

        if (!isEmpty(currentTransferUnsavedData)) {
          const mergedData = currentTransferUnsavedData.items;

          forEach(validInventoryItems, (newItem) => {
            const targetItem = find(mergedData, {
              entity_id: newItem.entity_id,
            });
            if (!isNil(targetItem)) {
              merge(targetItem, newItem);
            } else {
              mergedData.push(newItem);
            }
          });
          originReallyChangedTransfer[siteIdTo].items = mergedData;
        } else {
          originReallyChangedTransfer[siteIdTo] = {
            items: validInventoryItems,
          };
        }

        setFormFieldValue(
          this.form,
          this.formFieldPath.virtualReallyChangedTransfers,
          originReallyChangedTransfer,
          false,
        );
      }
    }

    const treeData = this.convertInventoryItemVenuesToTree(
      updatedParInventoryItems,
    );

    this.updateItemsByCompanyPreferences(
      treeData,
      this.formFieldPath.inventoryItemVenues,
      markField,
    );
    getFormControl(
      this.form,
      this.formFieldPath.inventoryItemVenues,
    )?.updateValueAndValidity({
      onlySelf: true,
      emitEvent: true,
    });

    this.isInventoryItemVenuesGridBusy$.next(false);
    setTimeout(() => {
      this.listGridApi.expandAll();
    });
  }

  public getReceivedCost(
    grandTotalData: IGridFlatInventoryItemVenueTotal,
  ): number {
    const zeroStatus = [
      TRANSFER_STATUS_VALUE.Null,
      TRANSFER_STATUS_VALUE.Open,
      TRANSFER_STATUS_VALUE.New,
    ];
    const transferType = this.getFormFieldValue(
      this.formFieldPath.transferType,
    );

    if (
      isEqual(transferType, TRANSFER_TYPE.PRE_EVENT_TRANSFER) ||
      includes(zeroStatus, this.getFormFieldValue(this.formFieldPath.status))
    ) {
      return 0;
    }

    return !isNil(get(grandTotalData, this.formFieldPath.receivedTotalCost, 0))
      ? get(grandTotalData, this.formFieldPath.receivedTotalCost, 0).valueOf()
      : 0;
  }

  public initKeyDownEventSubscription(): void {
    this.scanningService.scanOptions = {
      isCategoryListView: this.isCategoryListView(),
      focusKeyForListModal: this.formFieldPath.initialCount,
      focusKeyForTreeModal: this.formFieldPath.initialCount,
      isCompleted: this.isFinalStatusFun(),
      focusOnNextRow: false,
      customEntityIdPath: 'item_entity_id',
    };
    const scanningSubscription =
      this.scanningService.returnScanningSubscription(
        this.scanningService.scanOptions,
      );
    this.subscriptions.push(scanningSubscription);
  }

  private initFetchingQohAndQtyDataStatus(): void {
    this.isPendingQuantityFetching = true;
    this.isQohDataFetching = true;
  }

  private pickGrandTotalFields(
    totalsData: IGridInventoryItemVenueTotalFormData,
  ): void {
    const grandTotalData = head(totalsData.pinnedBottomData);

    this.grandTotalData = {
      request_cost: !isNil(get(grandTotalData, this.formFieldPath.totalCost))
        ? get(grandTotalData, this.formFieldPath.totalCost).valueOf()
        : 0,
      received_cost: this.getReceivedCost(grandTotalData),
      reversed_cost: !isNil(
        get(grandTotalData, this.formFieldPath.reversedCost, 0),
      )
        ? get(grandTotalData, this.formFieldPath.reversedCost, 0).valueOf()
        : 0,
    };

    const siteIdToValue: ObjectId = this.getFormFieldValue(
      this.formFieldPath.siteIdTo,
    );
    const virtualReallyChangedTransfers: ReallyChangedTransfers =
      getFormFieldValue(
        this.form,
        this.formFieldPath.virtualReallyChangedTransfers,
      );

    if (has(virtualReallyChangedTransfers, siteIdToValue)) {
      set(virtualReallyChangedTransfers, [siteIdToValue, 'grand_total'], {
        ...this.grandTotalData,
      });

      setFormFieldValue(
        this.form,
        this.formFieldPath.virtualReallyChangedTransfers,
        virtualReallyChangedTransfers,
        false,
      );
    }
  }
}
